import {
  ThemeDefaultParams,
  iconOverrides,
  iconSetMaterial,
  themeQuartz,
} from 'ag-grid-enterprise';

// Override all AG Grid icons with Material Symbols Rounded glyphs so icons are
// visually consistent with the rest of the app.  iconSetMaterial is kept as a
// fallback for any icons AG Grid may add in the future.
//
// If any icons are missing from the generated Material Icons font,
// see komodo-ui/public/fonts/material-ui-compression to add them.
const joinMaterialIcons = iconOverrides({
  // if any icons are missing from the generated Material Icons font,
  // see komodo-ui/public/fonts/material-ui-compression to add them
  cssImports: ['fonts/fonts-006.css'],
  type: 'font',
  family: 'Material',
  icons: {
    asc: 'arrow_upward',
    desc: 'arrow_downward',
    none: 'sort',
    'column-arrow': 'arrow_downward',

    // Navigation arrows
    up: 'arrow_upward',
    down: 'arrow_downward',
    left: 'arrow_back',
    right: 'arrow_forward',

    // Small directional
    'small-down': 'keyboard_arrow_down',
    'small-left': 'keyboard_arrow_left',
    'small-right': 'keyboard_arrow_right',
    'small-up': 'keyboard_arrow_up',

    // Menu & header
    menu: 'menu',
    'menu-alt': 'arrow_drop_down',
    filter: 'filter_alt',
    'filter-add': 'add',
    columns: 'view_column',

    // Actions
    cancel: 'close',
    cross: 'close',
    plus: 'add',
    copy: 'content_copy',

    // Column features
    grip: 'drag_indicator',
    pin: 'push_pin',
    'un-pin': 'push_pin',
    aggregation: 'functions',

    // Visibility
    eye: 'visibility',
    'eye-slash': 'visibility_off',
    linked: 'link',
    unlinked: 'link_off',

    // Misc
    'color-picker': 'palette',
    loading: 'sync',
  },
});

const getJoinGridTheme = (themeOverrides: Partial<ThemeDefaultParams> = {}) =>
  themeQuartz
    .withParams({
      // Size + Font
      rowHeight: 40,
      headerHeight: 40,
      iconSize: 16, // matches our icon-sm size
      fontSize: 14,
      fontFamily: ['Larsseit', 'sans-serif'],

      // Borders
      borderRadius: 0,
      rowBorder: true,
      wrapperBorder: false,
      pinnedColumnBorder: false,
      pinnedRowBorder: false,
      inputBorder: false,
      inputFocusBorder: false,
      inputFocusShadow: false,
      cellEditingShadow: false,

      // Main Colors
      textColor: 'var(--colors-type-primary)',
      foregroundColor: 'var(--colors-type-primary)',
      backgroundColor: 'var(--colors-background-primary)',
      borderColor: 'var(--colors-border-muted)',
      iconColor: 'var(--colors-type-primary)',
      headerBackgroundColor: 'var(--colors-background-primary)',
      headerTextColor: 'var(--colors-type-muted)',
      menuBackgroundColor: 'var(--colors-background-primary)',
      panelBackgroundColor: 'var(--colors-background-primary)',
      tooltipBackgroundColor: 'var(--background-1)',

      // Hover + Focus
      rowHoverColor: 'var(--colors-grid-hover)',
      headerCellHoverBackgroundColor: 'var(--colors-grid-hover)',
      // Per the docs, `accentColor` is the "color used for highlights and selection."
      // https://www.ag-grid.com/react-data-grid/theming-colors/#color-parameters
      // It's automatically lightened a bit for selection, so the full-saturation selection-focus-fill works best here
      accentColor: 'var(--colors-selection-focus-fill)',
      // Cell selection border uses Join brand yellow
      rangeSelectionBorderColor: 'var(--colors-border-brand)',
      rangeSelectionBackgroundColor:
        'color-mix(in srgb, var(--colors-border-brand) 10%, transparent)',
      iconButtonActiveColor: 'var(--colors-type-primary)',
      iconButtonActiveBackgroundColor: 'transparent',
      iconButtonActiveIndicatorColor: 'var(--colors-badge-notification)',
      focusShadow: false,

      // Header
      headerFontSize: 13,
      headerFontWeight: 700,
      headerFontFamily: ['Larsseit', 'sans-serif'],
      headerColumnResizeHandleWidth: 0,

      // Checkboxes
      checkboxCheckedBackgroundColor: 'var(--colors-checkbox-selected)',
      checkboxUncheckedBackgroundColor: 'var(--colors-background-primary)',
      checkboxUncheckedBorderColor: 'var(--colors-checkbox-unselected)',
      checkboxIndeterminateBackgroundColor: 'var(--colors-checkbox-selected)',
      checkboxBorderWidth: 2,
      checkboxBorderRadius: 2,

      // Buttons
      iconButtonHoverBackgroundColor: 'var(--colors-button-icon-hover)',
      columnDropCellBackgroundColor: 'var(--colors-background-2)',
      columnDropCellDragHandleColor: 'var(--colors-checkbox-unselected)',
      iconButtonBorderRadius: 999,
      columnDropCellBorder: false,

      // Sidebar
      sideBarBackgroundColor: 'var(--colors-background-1)',
      sideButtonBarBackgroundColor: 'var(--colors-background-1)',
      sideButtonHoverBackgroundColor: 'var(--colors-background-1)',
      sideButtonSelectedBackgroundColor: 'var(--colors-background-2)',
      sideButtonBorder: false,
      sideButtonSelectedBorder: false,
      sidePanelBorder: false,
      toolPanelSeparatorBorder: false,

      // Input
      inputPaddingStart: 15,

      // Menu
      // TODO: Upgrade Tailwind to v4 to enable usage of the named .shadow class
      menuBorder: true,
      menuTextColor: 'var(--colors-type-primary)',
      menuShadow: '2px 6px 4px 0px rgba(52, 75, 54, 0.25)',

      ...themeOverrides,
    })
    // we can add custom SVG icons here if needed:
    // https://www.ag-grid.com/react-data-grid/custom-icons/#overriding-icons-with-images
    .withPart(iconSetMaterial)
    .withPart(joinMaterialIcons);

export const defaultTheme = getJoinGridTheme();
// TODO: bordered theme should be removed, in favor of more explicit distinctions for border/size distinctions
export const borderedTheme = getJoinGridTheme({
  rowBorder: true,
  wrapperBorder: false,
  rowHeight: 56,
});

export const gridTheme = getJoinGridTheme({
  // Horizontal borders only — no vertical gridlines
  borderColor: 'var(--colors-border-muted)',
  headerRowBorder: true,
  wrapperBorder: true,
  wrapperBorderRadius: 0,
});

export const getTableTheme = (height: 'dense' | 'default') =>
  getJoinGridTheme({
    rowHeight: height === 'dense' ? 40 : 56,
    headerHeight: height === 'dense' ? 40 : 56,
    oddRowBackgroundColor: 'transparent',

    borderColor: 'var(--colors-border)',
    headerRowBorder: true,
    rowBorder: true,
    wrapperBorder: true,
  });
